/**
 * Tool-execution timeout helpers.
 *
 * Wraps a single browser-tool invocation with a hard per-tool budget and
 * converts inner rejections into recovery-string results. Used by both the
 * API agent's createBrowserTools factory and the CLI session daemon's
 * dispatchBrowserToolWithTimeout, so wedged pages fail fast and identically
 * across both call paths.
 *
 * @module
 */

import { TIMEOUTS } from '../constants.js';

/**
 * Sentinel prefix used by the tool wrapper for any non-normal outcome
 * (timeout OR inner rejection). Tool renderers and summarizers detect this
 * to preserve the recovery message across truncation boundaries. A single
 * sentinel is used because the recovery path is the same in both cases:
 * the agent must observe page state before retrying.
 */
export const TOOL_TIMEOUT_SENTINEL = '[TOOL_TIMEOUT]';
const TIMEOUT_ABORT_CLEANUP_GRACE_MS = 100;

const waitForTimeoutCleanup = async (inner: Promise<unknown>): Promise<void> => {
  await Promise.race([
    inner.then(
      () => undefined,
      () => undefined
    ),
    new Promise<void>((resolve) => setTimeout(resolve, TIMEOUT_ABORT_CLEANUP_GRACE_MS)),
  ]);
};

/**
 * If the tool result is a sentinel string (timeout or inner rejection
 * converted to a tool result), return a terse summary suitable for
 * truncated context history. Returns null otherwise so callers can fall
 * through to their normal render logic.
 */
export function renderIfTimeout(result: unknown): string | null {
  if (typeof result !== 'string') return null;
  if (!result.startsWith(TOOL_TIMEOUT_SENTINEL)) return null;
  return '[TOOL FAILED — agent must call browser_snapshot or browser_handle_dialog before retrying]';
}

/**
 * Thrown internally by withToolTimeout before being caught and converted to a
 * recovery string. Exported so future callers can discriminate timeouts from
 * other tool-execution errors with `instanceof` or `_tag` tests.
 */
export class ToolTimeoutError extends Error {
  readonly _tag = 'ToolTimeoutError';
  readonly toolName: string;
  readonly budgetMs: number;
  constructor(toolName: string, budgetMs: number) {
    super(`Tool '${toolName}' timed out after ${budgetMs}ms`);
    this.name = 'ToolTimeoutError';
    this.toolName = toolName;
    this.budgetMs = budgetMs;
  }
}

/**
 * Wrap an async tool execution with a hard timeout AND an error-to-result
 * converter. The wrapper ALWAYS resolves with a string result when something
 * goes wrong (timeout or inner rejection) — it never rethrows.
 *
 * Why errors become results: with OpenAI's Responses API and `store: true`,
 * the `function_call` item is streamed into OpenAI's stored response chain
 * before `execute` runs. If `execute` then throws, the AI SDK fails to append
 * a matching `function_call_output` and the next turn is rejected with
 * `400 invalid_request_error: "No tool output found for function call
 * call_XXX"`, which is non-retryable without deviating from the stored chain.
 * Always resolving with a result — even a synthetic one describing the error —
 * keeps the conversation well-formed and lets the agent recover.
 *
 * Implementation notes:
 *   1. `inner.catch(() => {})` suppresses unhandled-rejection noise if the
 *      orphan rejects after the race already settled.
 *   2. A `settled` guard stops a late-firing timer from logging a spurious
 *      timeout when the real work finished microseconds earlier.
 *   3. The gate wrapper (applyBrowserSessionGate) sits outside this one. On
 *      timeout we wait briefly for abort-aware callees to unwind before the
 *      gate releases, so shared executor state (withCallSignal) is cleared
 *      before the next tool starts.
 */
export async function withToolTimeout<T>(
  toolName: string,
  budgetMs: number,
  fn: (signal: AbortSignal) => Promise<T>,
  onTimeout?: (msg: string, meta: Record<string, unknown>) => void,
  onError?: (err: unknown, meta: Record<string, unknown>) => void
): Promise<T | string> {
  let settled = false;
  let timedOut = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let inner: Promise<T> | undefined;
  // Abort signal: when the timer wins the race, we abort so cancel-aware
  // callees (e.g. client.runCode) can short-circuit and run their cleanup
  // (stripping agent-added dialog listeners) before the next tool call starts.
  // Callees that ignore the signal behave as before.
  const controller = new AbortController();
  try {
    inner = fn(controller.signal);
    // Prevent unhandled-rejection if the orphan rejects after the race.
    inner.catch(() => {});
    return await Promise.race<T | string>([
      inner.then(
        (v) => {
          settled = true;
          return v;
        },
        (e): T | string => {
          // If the timer already won, swallow the late orphan rejection —
          // the sentinel string has already been returned from the race.
          if (settled) return '' as T | string;
          settled = true;
          try {
            onError?.(e, { toolName, budgetMs });
          } catch {
            // Never let observability crash the recovery path.
          }
          // Never leak internal error details (service names, stack frames,
          // Effect defects) to the model — the agent will confabulate causes
          // ("the metrics service is broken") and chase phantoms. Log the real
          // error via onError; return a generic recovery instruction.
          return `${TOOL_TIMEOUT_SENTINEL} Tool '${toolName}' did not complete. Recover by calling browser_snapshot to observe page state, browser_handle_dialog (if a confirm/alert is open), or browser_navigate (to a known-good URL). Do not retry the same action without first observing the page.`;
        }
      ),
      new Promise<string>((resolve) => {
        timer = setTimeout(() => {
          if (settled) return;
          settled = true;
          timedOut = true;
          // Signal cancellation so cancel-aware callees can unwind promptly
          // (e.g. runCode's finally strips the agent's leaked dialog listener).
          controller.abort();
          try {
            onTimeout?.('[browserTools] Tool timed out', { toolName, budgetMs });
          } catch {
            // Never let observability crash the recovery path.
          }
          resolve(
            `${TOOL_TIMEOUT_SENTINEL} Tool '${toolName}' timed out after ${Math.round(
              budgetMs / 1000
            )}s. The page is likely wedged — a native dialog may be blocking JS execution, an XHR is hung, or the JS context is unresponsive. Recover by calling browser_handle_dialog (if a confirm/alert is open), browser_snapshot (to inspect state), or browser_navigate (to a known-good URL). Do not retry the same action without first observing the page.`
          );
        }, budgetMs);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
    if (timedOut && inner) {
      await waitForTimeoutCleanup(inner);
    }
  }
}

/**
 * Per-tool execution budget. Used by the timeout wrapper.
 * Numbers come from TIMEOUTS so all callers stay in sync with Playwright's
 * own action timeouts.
 *
 * `browser_wait_for` is special-cased because the agent passes its own
 * `timeout` arg — the budget is computed dynamically per call to honour the
 * caller's intent while still capping at a hard ceiling.
 */
export const pickToolBudget = (toolName: string, input?: unknown): number => {
  switch (toolName) {
    case 'browser_navigate':
    case 'browser_navigate_back':
      return TIMEOUTS.TOOL_NAVIGATE;
    case 'browser_handle_dialog':
      return TIMEOUTS.TOOL_HANDLE_DIALOG;
    case 'browser_click':
    case 'browser_hover':
    case 'browser_drag':
    case 'browser_type':
    case 'browser_press_key':
    case 'browser_fill_form':
    case 'browser_select_option':
    case 'browser_file_upload':
    case 'browser_scroll':
    case 'browser_dismiss_overlay':
      return TIMEOUTS.TOOL_INTERACTION;
    case 'browser_wait_for': {
      // wait_for accepts a caller-supplied `timeout` (ms) or `time` (sec).
      // Honor the agent's intent but cap at 5 min so a runaway wait still
      // fails fast enough to preserve the segment's video.
      const HARD_CAP_MS = 5 * 60_000;
      const BUFFER_MS = 5_000;
      const args = (input as { timeout?: number; time?: number } | undefined) ?? {};
      const requestedMs =
        typeof args.timeout === 'number'
          ? args.timeout
          : typeof args.time === 'number'
          ? args.time * 1000
          : undefined;
      if (typeof requestedMs === 'number' && Number.isFinite(requestedMs) && requestedMs > 0) {
        return Math.min(requestedMs + BUFFER_MS, HARD_CAP_MS);
      }
      return TIMEOUTS.TOOL_NAVIGATE;
    }
    default:
      return TIMEOUTS.TOOL_DEFAULT;
  }
};
